import { Platform } from '@angular/cdk/platform';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';

export interface CustomDateAdapterOptions {
  useDefaultDate?: boolean;
}

export const CUSTOM_DATE_ADAPTER_OPTIONS =
  new InjectionToken<CustomDateAdapterOptions>('CUSTOM_DATE_ADAPTER_OPTIONS');

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  private readonly defaultDate = new Date(2020, 0, 2); // Jan 2, 2020
  private readonly minDate = new Date(2020, 0, 1);

  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string,
    platform: Platform,
    @Optional() @Inject(CUSTOM_DATE_ADAPTER_OPTIONS)
    private options?: CustomDateAdapterOptions
  ) {
    super(matDateLocale, platform);
  }

  override parse(value: any): Date | null {
    if (typeof value !== 'string') {
      return this.defaultIfInvalid(super.parse(value));
    }

    const trimmed = value.trim();
    if (!trimmed) return this.useDefault() ? this.defaultDate : null;

    if (!/^[0-9\/\-.]*$/.test(trimmed)) return this.useDefault() ? this.defaultDate : new Date(NaN);
    const parts = trimmed.split(/[\/\-\.]/).map(Number);

    let date: Date | null = null;

    switch (parts.length) {
      case 1: {
        let month = this.clampMonth(parts[0]);
        const day = month === 1 ? 2 : 1;
        date = new Date(2020, month - 1, day);
        break;
      }

      case 2: {
        let [month, day] = parts;
        month = this.clampMonth(month);
        if (day < 1 || isNaN(day)) day = 1;
        if (month === 1 && day === 1) day = 2;
        if (month === 2 && isNaN(day)) day = 1;

        date = new Date(2020, month - 1, day);
        break;
      }

      case 3: {
        let [month, day, year] = parts;
        month = this.clampMonth(month);
        if (day < 1 || isNaN(day)) day = 1;
        if (isNaN(year) || year < 2020 || year > 9999) year = 2020;

        date = new Date(year, month - 1, day);
        break;
      }
    }

    return this.defaultIfInvalid(date);
  }

  override createDate(year: number, month: number, date: number): Date {
    const createdDate = super.createDate(year, month, date);

    if (createdDate < this.defaultDate) {
      return this.defaultDate;
    }

    return createdDate;
  }

  override deserialize(value: any): Date | null {
    const date = super.deserialize(value);
    if (date && date < this.defaultDate) {
      return this.useDefault() ? this.defaultDate : null;
    }
    return date;
  }

  private clampMonth(month: number): number {
    if (isNaN(month)) return 1;
    return Math.min(Math.max(month, 1), 12);
  }

  private defaultIfInvalid(date: Date | null): Date | null {
    if (date && !isNaN(date.getTime()) && date > this.minDate) {
      return date;
    }
    return this.useDefault() ? this.defaultDate : null;
  }
  
  private useDefault(): boolean {
    return this.options?.useDefaultDate ?? true;
  }
}
